import {DPMappingVerticalAlign} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {
  ComplexTextAlign,
  ComplexTextSizeUnit,
  ComplexTextStyle,
} from 'common/src/modules/complexText/complexText.enums';
import {
  MovieAlign,
  MovieFillType,
  MovieLayerType,
  MovieLineCap,
  MovieLineJoin,
  MovieShapeLayerType,
} from 'common/src/modules/movie/movie.enums';
import {
  MovieLayerBase,
  MovieShapeLayer,
  MovieTextLayer,
} from 'common/src/modules/movie/movieLayers.types';
import {SolidColor} from 'common/src/modules/movie/moviePrimitives.types';
import {MovieComp} from 'common/src/modules/movie/movie.types';
import simpleMovieValue from 'common/src/modules/movie/utils/simpleMovieValue';

function createSolidColor([r, g, b]: [number, number, number]): SolidColor {
  return {
    t: MovieFillType.Solid,
    a: simpleMovieValue([1]),
    o: {
      c: simpleMovieValue([r, g, b, 255]),
    },
  };
}

function createLayerBase({
  id,
  name,
  width,
  height,
}: {
  id: string;
  name: string;
  width: number;
  height: number;
}): Omit<MovieLayerBase, 'ty'> {
  return {
    i: id,
    name,
    mappingId: +id,
    en: true,
    aEn: false,
    mb: false,
    s: 0,
    e: 15000,
    is3d: false,
    tmt: 0,
    w: width,
    h: height,
    tr: {
      p: simpleMovieValue([width / 2, height / 2, 0]),
      an: simpleMovieValue([0, 0, 0]),
      s: simpleMovieValue([1, 1, 1]),
      rz: simpleMovieValue([0]),
      rx: simpleMovieValue([0]),
      ry: simpleMovieValue([0]),
      a: simpleMovieValue([1]),
    },
    eff: [],
    masks: [],
  };
}

function createMockShapeLayer({
  id,
  name,
  width,
  height,
  fillColor,
}: {
  id: string;
  name: string;
  width: number;
  height: number;
  fillColor: [number, number, number];
}): MovieShapeLayer {
  return {
    ...createLayerBase({id, name, width, height}),
    ty: MovieLayerType.Shape,
    shape: {
      shapes: [
        {
          ty: MovieShapeLayerType.Rectangle,
          p: simpleMovieValue([0, 0]),
          s: simpleMovieValue([width, height]),
          r: simpleMovieValue([0]),
          tr: {
            p: simpleMovieValue([0, 0]),
            s: simpleMovieValue([1, 1]),
            an: simpleMovieValue([0, 0]),
            r: simpleMovieValue([0]),
            sk: simpleMovieValue([0]),
            skx: simpleMovieValue([0]),
            a: simpleMovieValue([1]),
          },
          fill: createSolidColor(fillColor),
          stroke: {
            ...createSolidColor([0, 0, 0]),
            w: simpleMovieValue([0]),
            lj: MovieLineJoin.Miter,
            lc: MovieLineCap.Butt,
            lm: 4,
          },
        },
      ],
    },
  };
}

function createMockTextLayer({
  id,
  name,
  width,
  height,
  text,
}: {
  id: string;
  name: string;
  width: number;
  height: number;
  text: string;
}): MovieTextLayer {
  return {
    ...createLayerBase({id, name, width, height}),
    ty: MovieLayerType.Text,
    text: {
      fontFileUrl: '',
      l: 0,
      lsx: 0,
      fill: createSolidColor([15, 15, 15]),
      sof: false,
      pointTextWidth: width,
      pointTextHeight: height,
      aeSrcTxt: {
        txt: text,
        fontFileName: 'Arial',
        fontFamily: 'Arial',
        size: 72,
        fontStyle: 'normal',
        justif: MovieAlign.Center,
        verticalAlign: DPMappingVerticalAlign.Center,
        blShift: 0,
        sWidth: 0,
        fColArr: [15, 15, 15],
        sOverF: false,
        trk: 0,
        lead: 86.4,
        allCaps: false,
        smallCaps: false,
        subScr: false,
        supScr: false,
        tsume: 0,
        vScale: 1,
        hScale: 1,
        weight: '400',
        strikeThrough: 0,
        underline: 0,
        minFontSize: 8,
        applyFill: true,
        italic: false,
        boxTxtP: {
          x: -width / 2,
          y: -height / 2,
        },
        boxTxtS: {
          w: width,
          h: height,
        },
        txtObj: {
          ver: 1,
          allCaps: false,
          wrapText: true,
          globalScale: true,
          verticalAlign: DPMappingVerticalAlign.Center,
          minFontSize: 8,
          fonts: [
            {
              family: 'Arial',
              url: '',
              weight: '400',
              italic: false,
            },
          ],
          lines: [
            {
              align: ComplexTextAlign.Center,
              lead: 1.2,
              parts: [
                {
                  color: '#0f0f0fff',
                  size: 72,
                  sizeUnits: ComplexTextSizeUnit.AbsolutePixels,
                  style: ComplexTextStyle.Normal,
                  text,
                  weight: '400',
                  x_shift: 0,
                  y_shift: 0,
                  letterSpacing: 0,
                  strikeThrough: 0,
                  underline: 0,
                  family: 'Arial',
                },
              ],
            },
          ],
        },
      },
    },
  };
}

function withPrecompId(comp: MovieComp, precompId: number): MovieComp {
  return Object.assign(comp, {precompId});
}

export default function getMockCreativePrecomps(): MovieComp[] {
  return [
    withPrecompId(
      {
        id: '101',
        name: 'Precomp Hero',
        w: 1080,
        h: 1080,
        dur: 15,
        fps: 30,
        bgC: [1, 1, 1],
        mb: false,
        mbAdaptiveSampleLimit: 128,
        mbSamplesPerFrame: 16,
        mbShutterAngle: 180,
        mbShutterPhase: 0,
        l: [
          createMockTextLayer({
            id: '1',
            name: 'Hero Logo',
            width: 320,
            height: 160,
            text: 'LOGO',
          }),
          createMockShapeLayer({
            id: '2',
            name: 'Hero Product',
            width: 720,
            height: 720,
            fillColor: [224, 224, 224],
          }),
          createMockShapeLayer({
            id: '3',
            name: 'Hero Background',
            width: 1080,
            height: 1080,
            fillColor: [245, 245, 245],
          }),
        ],
      },
      1,
    ),
    withPrecompId(
      {
        id: '102',
        name: 'Precomp Offer',
        w: 1080,
        h: 1080,
        dur: 12,
        fps: 30,
        bgC: [0.98, 0.98, 0.98],
        mb: false,
        mbAdaptiveSampleLimit: 128,
        mbSamplesPerFrame: 16,
        mbShutterAngle: 180,
        mbShutterPhase: 0,
        l: [
          createMockShapeLayer({
            id: '1',
            name: 'Offer Badge',
            width: 300,
            height: 300,
            fillColor: [230, 230, 230],
          }),
          createMockTextLayer({
            id: '2',
            name: 'Offer CTA',
            width: 500,
            height: 160,
            text: 'SHOP NOW',
          }),
          createMockTextLayer({
            id: '3',
            name: 'Offer Disclaimer',
            width: 640,
            height: 120,
            text: 'Terms apply',
          }),
          createMockShapeLayer({
            id: '4',
            name: 'Offer Background',
            width: 1080,
            height: 1080,
            fillColor: [250, 250, 250],
          }),
        ],
      },
      2,
    ),
    withPrecompId(
      {
        id: '103',
        name: 'Precomp End Card',
        w: 1920,
        h: 1080,
        dur: 10,
        fps: 30,
        bgC: [0.95, 0.95, 0.95],
        mb: false,
        mbAdaptiveSampleLimit: 128,
        mbSamplesPerFrame: 16,
        mbShutterAngle: 180,
        mbShutterPhase: 0,
        l: [
          createMockShapeLayer({
            id: '1',
            name: 'EndCard Background',
            width: 1920,
            height: 1080,
            fillColor: [242, 242, 242],
          }),
          createMockShapeLayer({
            id: '2',
            name: 'EndCard Product',
            width: 960,
            height: 960,
            fillColor: [220, 220, 220],
          }),
          createMockTextLayer({
            id: '3',
            name: 'EndCard Logo',
            width: 380,
            height: 180,
            text: 'BRAND',
          }),
        ],
      },
      3,
    ),
  ];
}
